import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import HeroCheck from "./HeroCheck";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const GROUP_COLOR = { entering: "#2BD97C", accelerating: "#E8EBF0", crowding: "#E3A44A", fading: "#C75E5E" };

function LiveTeaser({ t }) {
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const visible = t.prints.slice(0, 3);

  return (
    <div data-testid="hero-live-teaser" className="fp-card p-5 sm:p-6">
      <div className="flex items-baseline justify-between mb-4">
        <span className="font-mono text-[10px] tracking-widest text-[#2BD97C]">LIVE FROM THE FEED</span>
        <span className="font-mono text-[10px] text-[#5A6272]">EVIDENCE AS OF {t.as_of}</span>
      </div>
      <div className="space-y-2.5">
        {visible.map((p) => {
          const warning = p.group === "crowding" || p.group === "fading";
          return (
            <div key={p.id} data-testid={`teaser-print-${p.symbol}`} className="fp-row-in flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[rgba(255,255,255,0.05)] pb-2.5">
              <span className="font-mono text-[10px] text-[#5A6272]">{p.date}</span>
              <span className="font-mono text-sm font-bold">{p.symbol}</span>
              <span className="text-[13px] text-[#E8EBF0]" style={{ color: GROUP_COLOR[p.group] }}>
                {p.headline.replace(`${p.symbol} `, "")}
              </span>
              <span className="ml-auto font-mono text-xs whitespace-nowrap" style={{ color: warning ? "#E3A44A" : "#2BD97C" }}>
                {warning ? `avoided −${Math.abs(p.since_pct)}% ✓` : `+${p.since_pct}% since`}
              </span>
            </div>
          );
        })}
        <div data-testid="teaser-locked-row" className="relative pt-1.5">
          <div className="flex items-center gap-2 blur-[6px] opacity-40 select-none" aria-hidden="true">
            <span className="font-mono text-[10px] text-[#5A6272]">2026-07-15</span>
            <span className="font-mono text-sm font-bold text-[#5A6272]">····</span>
            <span className="text-[13px] text-[#5A6272]">entered Early Conviction: 4 specialists, quiet tape</span>
          </div>
          <div className="absolute inset-0 flex items-center gap-2">
            <Lock size={13} className="text-[#E3A44A] shrink-0" />
            <span className="font-mono text-xs text-[#E3A44A]">
              {t.n_live_convictions ? `${t.n_live_convictions} live themes: the trades several specialists are building right now.` : `${t.locked_count} Prints fired in the last 14 days.`}
            </span>
          </div>
        </div>
      </div>
      <Link to={authed ? "/app/feed" : "/register"} data-testid="teaser-cta" className="mt-4 inline-block font-mono text-xs text-[#5A6272] hover:text-[#8B93A3] underline">
        {authed ? "Open the feed →" : "Create a free account to open the feed"}
      </Link>
    </div>
  );
}

export default function HeroV3() {
  const [teaser, setTeaser] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/prints/teaser`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => d.prints?.length && setTeaser(d))
      .catch(() => {});
  }, []);

  return (
    <section id="top" className="fp-section !pt-20 !pb-16">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="min-w-0">
          <p className="fp-eyebrow mb-6">Smart money leaves prints · we read them all</p>
          <h1
            data-testid="hero-headline-v3"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]"
          >
            You find out what the smart money bought.{" "}
            <span className="text-[#2BD97C]">After the stock has run.</span>
          </h1>
          <p data-testid="hero-subhead" className="mt-6 max-w-xl text-base md:text-lg text-[#E8EBF0] leading-relaxed">
            We grade <span className="text-[#2BD97C]">10,000+ funds</span>, follow the few that are
            consistently right in their niche, and tell you whether you're early or late in every
            stock you own.
          </p>
          <div className="mt-8">
            <HeroCheck navigateTo />
          </div>
        </div>
        <div>
          {teaser ? (
            <LiveTeaser t={teaser} />
          ) : (
            <div className="fp-card h-72 flex items-center justify-center font-mono text-sm text-[#5A6272]">
              Opening the feed…
            </div>
          )}
        </div>
      </div>
      <FunnelStrip />
      <p className="mt-10 font-mono text-xs text-[#5A6272] tracking-wide">
        Free forever · Pro $29/mo or $249/yr · no card required ·{" "}
        <Link to="/how-crowded-is-my-401k" data-testid="hero-401k-link" className="text-[#8B93A3] hover:text-[#2BD97C] underline">
          Own funds in a 401k? Read your plan →
        </Link>{" "}
        ·{" "}
        <Link to="/login" data-testid="hero-login-btn" className="text-[#8B93A3] hover:text-[#E8EBF0] underline">
          Log in
        </Link>
      </p>
    </section>
  );
}

function FunnelStrip() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/portfolio`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => d.stats && setStats(d.stats))
      .catch(() => {});
  }, []);
  const steps = [
    ["10,000+ filers", "every fund over $100M, forced to disclose"],
    ["graded on results", "1.53M point-in-time holdings"],
    ["the specialists", "the few consistently right in their niche"],
  ];
  return (
    <div data-testid="hero-funnel-strip" className="mt-14 flex flex-wrap items-start gap-x-4 gap-y-5">
      {steps.map(([n, l], i) => (
        <div key={n} className="flex items-start gap-4">
          {i > 0 && <span className="font-mono text-lg text-[#3A4150] mt-0.5 hidden sm:block">→</span>}
          <div>
            <div className="font-mono text-lg font-semibold text-[#E8EBF0]">{n}</div>
            <div className="text-xs text-[#5A6272] mt-0.5 max-w-[190px]">{l}</div>
          </div>
        </div>
      ))}
      <div className="flex items-start gap-4">
        <span className="font-mono text-lg text-[#3A4150] mt-0.5 hidden sm:block">→</span>
        <a href="#record" data-testid="hero-funnel-payoff" className="block group">
          <div className="font-mono text-lg font-semibold text-[#2BD97C]">
            {stats ? (
              <>+{stats.total_return_pct}% <span className="text-[#5A6272] font-normal">vs SPY's +{stats.spy_total_return_pct}%</span></>
            ) : (
              "the conviction book"
            )}
          </div>
          <div className="text-xs text-[#5A6272] mt-0.5 max-w-[240px] group-hover:text-[#8B93A3]">
            their convictions as one book · since Dec 2023 →
          </div>
        </a>
      </div>
    </div>
  );
}
